import React, { useEffect, useState } from 'react';
import { Button, Dialog, FormHelperText, Grid, Icon, TextField, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Scrollbars from 'rc-scrollbars';
import DialogLoader from '../../../components/Loader/DialogLoader';
import { InputBaseField } from '../../../components/InputField/InputField';
import { DraggableComponent, Label } from '../../../components/UiElements/UiElements';
import { withSnackbar } from '../../../components/Message/Alert';
import { PostDataAPI } from '../../../Services/APIService';
import useStyles from './styles';

const initialState = {
    deviceId: 0,
    imei1: '',
    imei2: '',
    iccid: '',
    cartonId: null,
    cartonNo: '',
};

const fields = [
    { name: 'imei1', label: 'IMEI1', maxLength: 50, supported: true, required: true },
    { name: 'imei2', label: 'IMEI2', maxLength: 50, supported: true, required: false },
    { name: 'iccid', label: 'ICCID', maxLength: 50, supported: true, required: false },
];

function AddInventory({ dialogOpenClose, handleClose, handleSuccessClose, data, showMessage, ...props }) {
    const classes = useStyles();
    const [state, setState] = useState(() => {
        const inventoryState = { ...initialState, ...(data || {}) };
        delete inventoryState.programSN;
        delete inventoryState.programSn;
        return inventoryState;
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [cartonOptions, setCartonOptions] = useState([]);
    const [isCartonsLoading, setIsCartonsLoading] = useState(false);
    const isEdit = state.deviceId > 0;

    const displayValue = value => value === '-' || value === null || value === undefined ? '' : value;

    useEffect(() => {
        setIsCartonsLoading(true);
        PostDataAPI('deviceinventory/getCartonList', {}).then(result => {
            setIsCartonsLoading(false);
            if (result.success) {
                setCartonOptions((result.data || []).map(item => ({
                    label: item.cartonNo,
                    value: item.cartonId,
                })));
            } else {
                showMessage('Error', result.message || 'Error loading cartons, please contact administrator', 'error', 3000);
            }
        }).catch(() => {
            setIsCartonsLoading(false);
            showMessage('Error', 'Error loading cartons, please contact administrator', 'error', 3000);
        });
        // Cartons are refreshed each time this dialog component is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setState(previous => ({ ...previous, [name]: value }));
        if (value.trim()) {
            setErrors(previous => ({ ...previous, [name]: false }));
        }
    };

    const saveInventory = () => {
        if (isLoading) {
            return;
        }
        const requiredFields = fields.filter(field => field.supported && field.required);
        const nextErrors = requiredFields.reduce((result, field) => {
            result[field.name] = !displayValue(state[field.name]).trim();
            return result;
        }, {});
        setErrors(nextErrors);

        if (Object.values(nextErrors).some(Boolean)) {
            return;
        }

        const request = {
            imei1: state.imei1.trim(),
            imei2: state.imei2.trim(),
            iccid: state.iccid.trim(),
            cartonId: state.cartonId || null,
        };
        if (isEdit) {
            request.deviceId = state.deviceId;
        }

        setIsLoading(true);
        PostDataAPI(isEdit ? 'deviceinventory/updateDevice' : 'deviceinventory/addDevice', request, true).then(result => {
            setIsLoading(false);
            if (result.success) {
                setState(initialState);
                setErrors({});
                handleSuccessClose(result.message || (isEdit ? 'Inventory updated successfully.' : 'Inventory added successfully.'));
            } else {
                showMessage('Error', result.message || 'Error saving inventory, please contact administrator', 'error', 3000);
            }
        }).catch(() => {
            setIsLoading(false);
            showMessage('Error', 'Error saving inventory, please contact administrator', 'error', 3000);
        });
    };

    return (
        <Dialog
            classes={{ paper: classes.dialogPaper }}
            PaperComponent={DraggableComponent}
            disableBackdropClick
            disableEscapeKeyDown
            open={dialogOpenClose}
            maxWidth="md"
            {...props}
        >
            <div className={classes.dialogContent}>
                {isLoading ? <DialogLoader /> : ''}
                <div className={classes.box}>
                    <div className={classes.header} id="draggable-dialog-title">
                        <Typography className={classes.title}>{isEdit ? 'Edit Inventory' : 'Add Inventory'}</Typography>
                        <Icon className={classes.closeIcon} onClick={isLoading ? undefined : handleClose} color="primary"><CloseIcon /></Icon>
                    </div>
                    <Scrollbars autoHeight autoHeightMax={570}>
                        <div className={classes.content}>
                            <Grid container spacing={2}>
                                {fields.map(field => (
                                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} key={field.name}>
                                        <Grid row className={classes.formFieldColumn}>
                                            <Label title={field.label} size={12} mandatory={field.required} />
                                            <InputBaseField
                                                name={field.name}
                                                value={displayValue(state[field.name])}
                                                onChange={handleChange}
                                                placeholder={field.label}
                                                type="text"
                                                MaxLength={field.maxLength}
                                                IsDisabled={isLoading}
                                            />
                                            {errors[field.name] && !displayValue(state[field.name]).trim() ? (
                                                <FormHelperText className={classes.errorText}>
                                                    Please enter {field.label.toLowerCase()}
                                                </FormHelperText>
                                            ) : ''}
                                        </Grid>
                                    </Grid>
                                ))}
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Grid row className={classes.formFieldColumn}>
                                        <Label title="Carton No." size={12} mandatory={false} />
                                        <Autocomplete
                                            options={cartonOptions}
                                            getOptionLabel={option => option.label || ''}
                                            getOptionSelected={(option, value) => option.value === value.value}
                                            value={cartonOptions.find(option => option.value === state.cartonId) || null}
                                            onChange={(event, option) => setState(previous => ({
                                                ...previous,
                                                cartonId: option ? option.value : null,
                                                cartonNo: option ? option.label : '',
                                            }))}
                                            loading={isCartonsLoading}
                                            disabled={isLoading}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Carton No."
                                                    variant="outlined"
                                                    size="small"
                                                    className={classes.cartonSelect}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </div>
                    </Scrollbars>
                    <div className={classes.footer}>
                        <div className={classes.footerRight}>
                            <Button className={classes.backBtn} disabled={isLoading} onClick={handleClose}>Close</Button>
                            <Button className={classes.changeBtn} disabled={isLoading} onClick={saveInventory}>{isEdit ? 'Update' : 'Add New'}</Button>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default withSnackbar(AddInventory);
